import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full rounded-none border border-line bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-smoke/60 focus:outline-none focus:ring-2 focus:ring-volt focus:border-volt transition-colors duration-300";

const Label = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-smoke"
  >
    {children}
  </label>
);

const INITIAL = { name: "", company: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry transmitted.", {
        description: "Our freight team will reach out within one business day.",
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error("Transmission failed.", {
        description: "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-line py-32">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Overline>Get In Touch</Overline>
          <h2 className="mt-6 font-display text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl">
            Let's move your freight. <span className="text-volt">Cleaner.</span>
          </h2>
          <p className="mt-8 max-w-md text-sm leading-relaxed text-smoke md:text-base">
            Decarbonize your supply chain, optimize transportation costs, or
            transition to zero-emission logistics — Electrohaul is your partner
            in building the future of freight.
          </p>
          <div className="mt-12 space-y-3 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
            <p><span className="text-volt">—</span> Freight & Partnerships</p>
            <p><span className="text-volt">—</span> Response within one business day</p>
            <p><span className="text-volt">—</span> Operating across India</p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="grid grid-cols-1 gap-6 border border-line bg-ash/50 p-8 sm:grid-cols-2 md:p-12"
          >
            <div>
              <Label htmlFor="c-name">Name *</Label>
              <input
                id="c-name"
                data-testid="contact-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className={FIELD}
              />
            </div>
            <div>
              <Label htmlFor="c-company">Company</Label>
              <input
                id="c-company"
                data-testid="contact-company-input"
                value={form.company}
                onChange={update("company")}
                placeholder="Organization"
                className={FIELD}
              />
            </div>
            <div>
              <Label htmlFor="c-email">Work Email *</Label>
              <input
                id="c-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                className={FIELD}
              />
            </div>
            <div>
              <Label htmlFor="c-phone">Phone</Label>
              <input
                id="c-phone"
                data-testid="contact-phone-input"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91"
                className={FIELD}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="c-message">Message *</Label>
              <textarea
                id="c-message"
                data-testid="contact-message-input"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Routes, volumes, timelines — tell us about your freight."
                className={`${FIELD} resize-none`}
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group relative w-full overflow-hidden bg-volt px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black disabled:opacity-60 sm:w-auto"
              >
                <span className="absolute inset-0 -translate-x-full bg-cyber transition-transform duration-300 ease-out group-hover:translate-x-0" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      Transmitting <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Transmit Enquiry
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
